var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { isBefore, isPast } from "date-fns";
import { IdentifiedPatientState, primaryConsentTypes } from "healthex-common/consent.dtos";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { slugify, unslugifyOptional } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { findMatchingNamedRouteParent } from "./RoutingUIManager";
export var PatientStudiesListViewTab;
(function (PatientStudiesListViewTab) {
    PatientStudiesListViewTab["ALL"] = "ALL";
    PatientStudiesListViewTab["ACTION_NEEDED"] = "ACTION_NEEDED";
    PatientStudiesListViewTab["AGREED"] = "AGREED";
    PatientStudiesListViewTab["DECLINED"] = "DECLINED";
})(PatientStudiesListViewTab || (PatientStudiesListViewTab = {}));
const _patientResearchDashboardRoutes = {
    RESEARCH: NamedRoutes.PATIENT_SHARING,
    MY_HEALTHEX: NamedRoutes.PATIENT_MY_HEALTHEX,
};
const patientResearchDashboardRoutes = _patientResearchDashboardRoutes;
export var MobileBottomNavigationButtonType;
(function (MobileBottomNavigationButtonType) {
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["PROFILE"] = 0] = "PROFILE";
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["RESEARCH"] = 1] = "RESEARCH";
})(MobileBottomNavigationButtonType || (MobileBottomNavigationButtonType = {}));
export class PatientResearchDashboardUIManager {
    constructor(routingUIManager, studyManager, documentRetrievalManager, authManager, patientPreferencesManager) {
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.documentRetrievalManager = documentRetrievalManager;
        this.authManager = authManager;
        this.patientPreferencesManager = patientPreferencesManager;
        this.patientResearchDashboardNavigationHelper = new NavigationHelper(this.routingUIManager, patientResearchDashboardRoutes);
        this.mostRecentRetrieval = NOT_LOADED;
        this.pollRetrievalInterval = null;
        reaction(() => routingUIManager.currentRoute, async () => {
            await this.refreshTotals();
        }, { fireImmediately: true });
        reaction(() => this.currentTab, async () => {
            await this.refreshStudies();
        }, { fireImmediately: true });
        reaction(() => [this.authManager.isLoggedIn, this.isMyHealthExPage], async () => {
            await this.loadMostRecentRetrieval();
        });
        reaction(() => this.isUnifiedRecordProcessing, async () => {
            if (this.pollRetrievalInterval !== null) {
                window.clearInterval(this.pollRetrievalInterval);
                this.pollRetrievalInterval = null;
            }
            if (this.isUnifiedRecordProcessing === true) {
                this.pollRetrievalInterval = window.setInterval(async () => {
                    await this.loadMostRecentRetrieval();
                }, 10000);
            }
        }, { fireImmediately: true });
    }
    async loadMostRecentRetrieval() {
        if (!this.isMyHealthExPage)
            return;
        if (!this.authManager.isLoggedIn || this.authManager.userType !== UserType.PATIENT) {
            this.mostRecentRetrieval = null;
            return;
        }
        this.mostRecentRetrieval = await this.documentRetrievalManager.getMostRecentDownloadableOrProcessingTaskBatch();
    }
    get hasUnifiedRecordExpired() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientDeliveryExpiration) && isPast(this.mostRecentRetrieval.patientDeliveryExpiration);
    }
    get unifiedRecordSummary() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientData) !== null && _a !== void 0 ? _a : null;
    }
    get retrievalContactMethod() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientContactMethod) !== null && _a !== void 0 ? _a : null;
    }
    get preferredContactMethod() {
        var _a, _b;
        if (this.retrievalContactMethod !== NOT_LOADED && this.retrievalContactMethod)
            return this.retrievalContactMethod;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.preferredContactMethod) !== null && _b !== void 0 ? _b : ContactMethod.EMAIL;
    }
    get contactMethodWhenUnifiedRecordSent() {
        var _a, _b;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientContactMethod) !== null && _b !== void 0 ? _b : null;
    }
    get isUnifiedRecordDownloadable() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.unifiedRecordSummary === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.pdfStatus) === PatientDataVisualizationPdfStatus.COMPLETED && !!this.unifiedRecordSummary;
    }
    get isUnifiedRecordProcessing() {
        if (this.isUnifiedRecordDownloadable === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return false;
        return !this.isUnifiedRecordDownloadable;
    }
    get isMyHealthExPage() {
        return this.selectedBottomNavigationButton === MobileBottomNavigationButtonType.PROFILE;
    }
    get currentTab() {
        var _a, _b, _c;
        if (!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PATIENT_SHARING])) {
            return null;
        }
        return ((_c = unslugifyOptional((_b = (_a = this.routingUIManager) === null || _a === void 0 ? void 0 : _a.currentRouteParams) === null || _b === void 0 ? void 0 : _b.section)) !== null && _c !== void 0 ? _c : PatientStudiesListViewTab.ALL);
    }
    get selectedBottomNavigationButton() {
        switch (this.patientResearchDashboardNavigationHelper.currentNavigationItem) {
            case "RESEARCH":
                return MobileBottomNavigationButtonType.RESEARCH;
            case "MY_HEALTHEX":
                return MobileBottomNavigationButtonType.PROFILE;
            default:
        }
        return null;
    }
    get noConsentsAvailable() {
        if (this.studyManager.allPatientConsentsByStudyForSearchTotals.value === NOT_LOADED)
            return NOT_LOADED;
        return this.studyManager.allPatientConsentsByStudyForSearchTotals.value.all === 0;
    }
    get sortedStudies() {
        if (this.currentStudies === NOT_LOADED)
            return NOT_LOADED;
        const withMultipleEntriesPerStudy = [];
        this.currentStudies.forEach((studyWithConsents) => {
            const { consents, ...study } = studyWithConsents;
            const pendingConsents = consents.filter((consent) => consent.pending);
            const consentedConsents = consents.filter((consent) => !consent.pending);
            if (pendingConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: pendingConsents });
            }
            if (consentedConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: consentedConsents });
            }
        });
        return (withMultipleEntriesPerStudy
            .sort((a, b) => {
            var _a, _b;
            const aTimestamp = (_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.timestamp;
            const bTimestamp = (_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.timestamp;
            if (aTimestamp && bTimestamp) {
                return isBefore(new Date(aTimestamp), new Date(bTimestamp)) ? 1 : -1;
            }
            return 0;
        })
            .sort((a, b) => {
            var _a, _b;
            const isAEnrolling = ((_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.state) ===
                IdentifiedPatientState.ENROLLING;
            const isBEnrolling = ((_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.state) ===
                IdentifiedPatientState.ENROLLING;
            if (isAEnrolling && isBEnrolling)
                return 0;
            if (isAEnrolling && !isBEnrolling)
                return -1;
            if (!isAEnrolling && isBEnrolling)
                return 1;
            return 0;
        }));
    }
    get currentStudies() {
        if (!this.currentTab)
            return NOT_LOADED;
        switch (this.currentTab) {
            case PatientStudiesListViewTab.ALL:
                return this.studyManager.allPatientConsentsByStudyForSearch.value;
            case PatientStudiesListViewTab.ACTION_NEEDED:
                return this.studyManager.pendingPatientConsentsByStudyForSearch.value;
            case PatientStudiesListViewTab.AGREED:
                return this.studyManager.optedInPatientConsentsByStudyForSearch.value;
            case PatientStudiesListViewTab.DECLINED:
                return this.studyManager.optedOutPatientConsentsByStudyForSearch.value;
        }
        return NOT_LOADED;
    }
    async refreshStudies() {
        if (!this.currentTab)
            return;
        switch (this.currentTab) {
            case PatientStudiesListViewTab.ALL:
                this.studyManager.allPatientConsentsByStudyForSearch.reload();
                break;
            case PatientStudiesListViewTab.ACTION_NEEDED:
                this.studyManager.pendingPatientConsentsByStudyForSearch.reload();
                break;
            case PatientStudiesListViewTab.AGREED:
                this.studyManager.optedInPatientConsentsByStudyForSearch.reload();
                break;
            case PatientStudiesListViewTab.DECLINED:
                this.studyManager.optedOutPatientConsentsByStudyForSearch.reload();
                break;
        }
    }
    async refreshTotals() {
        if (!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PATIENT_SHARING,
            NamedRoutes.PATIENT_MY_HEALTHEX,
        ])) {
            return;
        }
        this.studyManager.allPatientConsentsByStudyForSearchTotals.reload();
    }
    goToPage(value, section) {
        switch (value) {
            case MobileBottomNavigationButtonType.RESEARCH:
                if (!section) {
                    return;
                }
                this.patientResearchDashboardNavigationHelper.redirectTo("RESEARCH", {
                    section: slugify(section),
                });
                break;
            case MobileBottomNavigationButtonType.PROFILE:
                this.patientResearchDashboardNavigationHelper.navigateTo("MY_HEALTHEX");
                break;
        }
    }
    async getDownloadPdfLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadPdfLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
    async getDownloadRawDocumentsLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadDocumentsLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientResearchDashboardUIManager.prototype, "mostRecentRetrieval", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "hasUnifiedRecordExpired", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "unifiedRecordSummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "retrievalContactMethod", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "preferredContactMethod", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "contactMethodWhenUnifiedRecordSent", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "isUnifiedRecordDownloadable", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "isMyHealthExPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "currentTab", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "selectedBottomNavigationButton", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "noConsentsAvailable", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "sortedStudies", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchDashboardUIManager.prototype, "currentStudies", null);
